import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Landing from './pages/Landing';
import CaseStudies from './pages/CaseStudies';
import IndustryCaseStudy from './pages/IndustryCaseStudy';
import Products from './pages/Products';
import ProductShowcase from './pages/ProductShowcase';
import Auth from './pages/Auth';
import AuthAppSumo from './pages/AuthAppSumo';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import PlatformOverviewDashboard from './pages/PlatformOverviewDashboard';
import AIGenerator from './pages/AIGenerator';
import WebsiteEditor from './pages/WebsiteEditor';
import VisualBuilder from './pages/VisualBuilder';
import TemplateEditor from './pages/TemplateEditor';
import WebsitePreview from './pages/WebsitePreview';
import PublicWebsite from './pages/PublicWebsite';
import AdminPanel from './pages/AdminPanel';
import AdminBookingsPage from './pages/AdminBookingsPage';
import WebsiteSettings from './pages/WebsiteSettings';
import StoreCheckout from './pages/StoreCheckout';
import OrderTracking from './pages/OrderTracking';
import DriverDeliveryApp from './pages/DriverDeliveryApp';
import Settings from './pages/Settings';
import EcommercePanel from './pages/EcommercePanel';
import CRMPanel from './pages/CRMPanel';
import BookingPanel from './pages/BookingPanel';
import MarketingPanel from './pages/MarketingPanel';
import AITools from './pages/AITools';
import AIToolsDashboard from './pages/AIToolsDashboard';
import AIToolPage from './pages/AIToolPage';
import MediaStudioPage from './pages/MediaStudioPage';
import PresentationPage from './pages/PresentationPage';
import AIWebAgent from './pages/AIWebAgent';
import DomainsHostingDashboard from './pages/DomainsHostingDashboard';
import MarketingSuiteDashboard from './pages/MarketingSuiteDashboard';
import AiSeoDashboard from './pages/AiSeoDashboard';
import AiAdsDashboard from './pages/AiAdsDashboard';
import EmailMarketingDashboard from './pages/EmailMarketingDashboard';
import AutomationsDashboard from './pages/AutomationsDashboard';
import ConnectionsDashboard from './pages/ConnectionsDashboard';
import ConnectionsOAuthCallback from './pages/ConnectionsOAuthCallback';
import AIStudioDashboard from './pages/AIStudioDashboard';
import AICofounderDashboard from './pages/AICofounderDashboard';
import AICofounderOutputs from './pages/AICofounderOutputs';
import WhatsAppMarketingDashboard from './pages/WhatsAppMarketingDashboard';
import DyadAgentPanel from './pages/DyadAgentPanel';
import SMSMarketingDashboard from './pages/SMSMarketingDashboard';
import LeadScraper from './pages/LeadScraper';
import CrmDashboard from './pages/CrmDashboard';
import DemoPitch from './pages/DemoPitch';
import LovableCloneLab from './pages/LovableCloneLab';
import AdorableLab from './pages/AdorableLab';
import AliFullStackLab from './pages/AliFullStackLab';
import LegalPage from './pages/LegalPage';
import NotFoundPage from './pages/NotFoundPage';
import ErrorBoundary from './components/ErrorBoundary';
import AuthCallback from './pages/AuthCallback';
import CookieConsentBanner from './components/CookieConsentBanner';


import LeadScraperDashboard from './pages/LeadScraperDashboard';
import ExistingWebsiteAppBuilder from './pages/ExistingWebsiteAppBuilder';
import WhiteLabelLanding from './pages/WhiteLabelLanding';
import SupportTickets from './pages/SupportTickets';
import BotLeadsAndCalls from './pages/BotLeadsAndCalls';
import SnagcartPage from './pages/SnagcartPage';
import GPTPilot from './pages/GPTPilot';
import OdooConfigurator from './pages/OdooConfigurator';
import OdooRedirect from './pages/OdooRedirect';
import OperatorCockpit from './pages/OperatorCockpit';
import OperatorCommerceEmbed from './pages/OperatorCommerceEmbed';
import SubscriptionPlans from './pages/SubscriptionPlans';
import SnagcartApps from './pages/SnagcartApps';
import PlatformGuide from './pages/PlatformGuide';
import PlatformHelpWidget from './components/PlatformHelpWidget';
import WelcomeTourModal from './components/WelcomeTourModal';
import JessicaChatbot from './components/JessicaChatbot';
import AIEngineer from './pages/AIEngineer';
import AppBuilder from './pages/AppBuilder';
import AliFullStackBuilder from './pages/AliFullStackBuilder';
import MetaGPT from './pages/MetaGPT';
import { useParams } from 'react-router-dom';
import AIBuilderIDE from './components/ai-builder/AIBuilderIDE';

function AIBuilderRouteWrapper() {
  const { projectId } = useParams();
  if (!projectId) {
    return <div style={{ color: 'white', padding: 24 }}>Missing projectId</div>;
  }
  return <AIBuilderIDE projectId={projectId} />;
}


import VoiceCampaigns from './pages/VoiceCampaigns';
import { CreateAIProject } from './pages/CreateAIProject';
import OdooIntegrationPanel from './pages/OdooIntegrationPanel';
// WebsiteGPT removed — GPT Pilot is now integrated into SiteBrain
import './App.css';
import useEntitlements, { AI_TOOL_ADDON_IDS, modulesForPath } from './hooks/useEntitlements';
import { WhitelabelBrandProvider } from './context/WhitelabelBrandContext';
import WhitelabelHostGate from './components/WhitelabelHostGate';
import ImpersonationBanner from './components/ImpersonationBanner';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';
const AUTH_CHECK_TIMEOUT_MS = 10000;
const AUTH_DENIED_STATUSES = new Set([401, 403]);

async function fetchCurrentUser(token) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), AUTH_CHECK_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_URL}/api/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` },
      signal: controller.signal,
    });
    if (AUTH_DENIED_STATUSES.has(res.status)) {
      const error = new Error('auth denied');
      error.authDenied = true;
      throw error;
    }
    if (!res.ok) {
      throw new Error('auth unavailable');
    }
    return await res.json();
  } finally {
    clearTimeout(timeout);
  }
}

const PrivateRoute = ({ children }) => {
  let token = localStorage.getItem('token');
  if (!token) {
    // Recover from OpenSail iframe overwriting the Emerge token
    const backup = localStorage.getItem('emerge_token_backup');
    if (backup) {
      localStorage.setItem('token', backup);
      localStorage.removeItem('emerge_token_backup');
      token = backup;
    }
  }
  return token ? (
    <div data-dashboard-root className="min-h-screen">
      {children}
    </div>
  ) : <Navigate to="/auth" />;
};

/** Soft-gate: redirect when the route's module is locked. Fail-closed while loading. */
const ModuleRoute = ({ children }) => {
  const location = useLocation();
  const { loading, canAny, entitlements, error, refresh } = useEntitlements();
  const required = modulesForPath(location.pathname);
  if (!required) return children;
  if (error && !entitlements) {
    return (
      <div className="min-h-[40vh] flex flex-col items-center justify-center gap-3 px-4 text-center text-sm text-zinc-300">
        <div>Unable to check access right now.</div>
        <button
          type="button"
          onClick={refresh}
          className="rounded-lg border border-zinc-700 px-4 py-2 text-zinc-100 hover:bg-zinc-900"
        >
          Retry access check
        </button>
      </div>
    );
  }
  if (loading || !entitlements) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-zinc-400">
        Checking access…
      </div>
    );
  }
  if (canAny(required)) return children;
  // Premium AI tool add-ons → Modules store; everything else → Choose Plan
  const isAddon = required.some((id) => AI_TOOL_ADDON_IDS.has(id));
  if (isAddon) {
    return <Navigate to="/snagcart/apps" replace state={{ lockedModules: required, highlightAddon: required[0] }} />;
  }
  return <Navigate to="/subscription-plans" replace state={{ lockedModules: required }} />;
};

/** Legacy Emergent hash session_id is no longer accepted in the SPA.
 *  First-party Google OAuth lands on /auth/google/callback#access_token=... */
const SessionCallbackGate = ({ children }) => children;

function App() {
  const [user, setUser] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    let token = localStorage.getItem('token');
    if (!token) {
      const backup = localStorage.getItem('emerge_token_backup');
      if (backup) {
        localStorage.setItem('token', backup);
        localStorage.removeItem('emerge_token_backup');
        token = backup;
      }
    }
    if (token) {
      fetchCurrentUser(token)
        .then(data => {
          if (!cancelled) setUser(data);
        })
        .catch((error) => {
          if (!error.authDenied) return;
          // Token may be an OpenSail JWT that overwrote the Emerge token.
          // Try restoring from the backup before giving up.
          const backup = localStorage.getItem('emerge_token_backup');
          if (backup && backup !== token) {
            localStorage.setItem('token', backup);
            localStorage.removeItem('emerge_token_backup');
            fetchCurrentUser(backup)
              .then(data => {
                if (!cancelled) setUser(data);
              })
              .catch((backupError) => {
                if (!backupError.authDenied) return;
                localStorage.removeItem('token');
                localStorage.removeItem('emerge_token_backup');
              });
          } else {
            localStorage.removeItem('token');
          }
        });
    }
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="App">
      <div className="grain-overlay"></div>
      <BrowserRouter>
        <WhitelabelBrandProvider user={user}>
        <ErrorBoundary>
        <SessionCallbackGate>
        <WhitelabelHostGate>
        <ImpersonationBanner user={user} setUser={setUser} />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:slug" element={<ProductShowcase />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/case-studies/:slug" element={<IndustryCaseStudy />} />
          <Route path="/login" element={<Auth setUser={setUser} />} />
          <Route path="/auth" element={<Auth setUser={setUser} />} />
          <Route path="/auth/appsumo" element={<AuthAppSumo setUser={setUser} />} />
          <Route path="/auth/google/callback" element={<AuthCallback setUser={setUser} />} />
          <Route path="/w/:slug/auth" element={<Auth setUser={setUser} />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<PrivateRoute><PlatformOverviewDashboard user={user} setUser={setUser} /></PrivateRoute>} />
          <Route path="/websites" element={<PrivateRoute><Dashboard user={user} /></PrivateRoute>} />
          <Route path="/websites/lovable-clone-lab" element={
            process.env.REACT_APP_ENABLE_LABS === 'true'
              ? <PrivateRoute><LovableCloneLab user={user} /></PrivateRoute>
              : <Navigate to="/websites" replace />
          } />
          <Route path="/websites/adorable-lab" element={
            process.env.REACT_APP_ENABLE_LABS === 'true'
              ? <PrivateRoute><AdorableLab user={user} /></PrivateRoute>
              : <Navigate to="/websites" replace />
          } />
          <Route path="/websites/alifullstack-lab" element={
            process.env.REACT_APP_ENABLE_LABS === 'true'
              ? <PrivateRoute><AliFullStackLab user={user} /></PrivateRoute>
              : <Navigate to="/websites" replace />
          } />
          <Route path="/privacy" element={<LegalPage kind="privacy" />} />
          <Route path="/terms" element={<LegalPage kind="terms" />} />
          <Route path="/refund" element={<LegalPage kind="refund" />} />
          <Route path="/return-policy" element={<LegalPage kind="return" />} />
          <Route path="/shipping-policy" element={<LegalPage kind="shipping" />} />
          <Route path="/generate" element={<PrivateRoute><AIGenerator /></PrivateRoute>} />
          <Route path="/editor/:websiteId" element={<PrivateRoute><WebsiteEditor /></PrivateRoute>} />
          <Route path="/builder/:websiteId" element={<PrivateRoute><VisualBuilder /></PrivateRoute>} />
          <Route path="/sites" element={<Navigate to="/websites" replace />} />
          <Route path="/sites/:websiteId/operate" element={<PrivateRoute><OperatorCockpit /></PrivateRoute>} />
          <Route path="/sites/:websiteId/operate-commerce" element={<PrivateRoute><OperatorCommerceEmbed /></PrivateRoute>} />
          <Route path="/edit/:websiteId" element={<PrivateRoute><TemplateEditor /></PrivateRoute>} />
          <Route path="/dyad/:websiteId" element={<PrivateRoute><DyadAgentPanel /></PrivateRoute>} />
          <Route path="/edit/:websiteId/settings" element={<PrivateRoute><WebsiteSettings /></PrivateRoute>} />
          <Route path="/websites/:websiteId/settings" element={<PrivateRoute><WebsiteSettings /></PrivateRoute>} />
          <Route path="/store/:websiteId/checkout" element={<StoreCheckout />} />
          <Route path="/track/:websiteId/:trackingToken" element={<OrderTracking />} />
          <Route path="/driver/:websiteId" element={<DriverDeliveryApp />} />
          <Route path="/preview/:websiteId" element={<PrivateRoute><WebsitePreview /></PrivateRoute>} />
          <Route path="/site/:slug" element={<PublicWebsite />} />
          <Route path="/admin/:websiteId" element={<PrivateRoute><AdminPanel /></PrivateRoute>} />
          <Route path="/admin/:websiteId/bookings" element={<PrivateRoute><AdminBookingsPage /></PrivateRoute>} />
          <Route path="/ecommerce/:websiteId" element={<PrivateRoute><EcommercePanel /></PrivateRoute>} />
          <Route path="/odoo-redirect" element={<OdooRedirect />} />
          <Route path="/odoo-integration" element={<PrivateRoute><OdooIntegrationPanel /></PrivateRoute>} />
          <Route path="/odoo-integration/:websiteId" element={<PrivateRoute><OdooIntegrationPanel /></PrivateRoute>} />
          <Route path="/crm/:websiteId" element={<PrivateRoute><CRMPanel /></PrivateRoute>} />
          <Route path="/booking/:websiteId" element={<PrivateRoute><BookingPanel /></PrivateRoute>} />
          <Route path="/marketing/:websiteId" element={<PrivateRoute><MarketingPanel /></PrivateRoute>} />
          <Route path="/ai-tools/:websiteId" element={<PrivateRoute><AITools /></PrivateRoute>} />
          <Route path="/ai-tools-dashboard" element={<PrivateRoute><ModuleRoute><AIToolsDashboard /></ModuleRoute></PrivateRoute>} />
          <Route path="/ai-tool/:toolId" element={<PrivateRoute><ModuleRoute><AIToolPage /></ModuleRoute></PrivateRoute>} />
          <Route path="/media-studio" element={<PrivateRoute><ModuleRoute><MediaStudioPage /></ModuleRoute></PrivateRoute>} />
          <Route path="/ai-presentation" element={<PrivateRoute><ModuleRoute><PresentationPage /></ModuleRoute></PrivateRoute>} />
          {/* Merged into Marketing Suite → AI Ads (Social schedule) */}
          <Route path="/ai-social-media" element={<Navigate to="/marketing-suite/ads" replace />} />
          <Route path="/ai-web-agent" element={<PrivateRoute><ModuleRoute><AIWebAgent /></ModuleRoute></PrivateRoute>} />
          <Route path="/domains-hosting" element={<PrivateRoute><ModuleRoute><DomainsHostingDashboard /></ModuleRoute></PrivateRoute>} />
          <Route path="/domains" element={<Navigate to="/domains-hosting" replace />} />
          <Route path="/marketing-suite" element={<PrivateRoute><ModuleRoute><MarketingSuiteDashboard /></ModuleRoute></PrivateRoute>} />
          <Route path="/marketing-suite/email" element={<PrivateRoute><ModuleRoute><EmailMarketingDashboard /></ModuleRoute></PrivateRoute>} />
          <Route path="/marketing-suite/sms" element={<PrivateRoute><ModuleRoute><SMSMarketingDashboard /></ModuleRoute></PrivateRoute>} />
          <Route path="/marketing-suite/seo" element={<PrivateRoute><ModuleRoute><AiSeoDashboard /></ModuleRoute></PrivateRoute>} />
          <Route path="/marketing-suite/ads" element={<PrivateRoute><ModuleRoute><AiAdsDashboard /></ModuleRoute></PrivateRoute>} />
          <Route path="/marketing-suite/crm" element={<PrivateRoute><ModuleRoute><CrmDashboard user={user} /></ModuleRoute></PrivateRoute>} />
          <Route path="/crm" element={<Navigate to="/marketing-suite/crm" replace />} />
          <Route path="/lead-scraper" element={<PrivateRoute><ModuleRoute><LeadScraper /></ModuleRoute></PrivateRoute>} />
          <Route path="/automations" element={<PrivateRoute><ModuleRoute><AutomationsDashboard /></ModuleRoute></PrivateRoute>} />
          <Route path="/connections" element={<PrivateRoute><ModuleRoute><ConnectionsDashboard /></ModuleRoute></PrivateRoute>} />
          <Route path="/connections/oauth/callback" element={<PrivateRoute><ConnectionsOAuthCallback /></PrivateRoute>} />
          <Route path="/demo" element={<PrivateRoute><DemoPitch /></PrivateRoute>} />
          <Route path="/ai-studio" element={<PrivateRoute><ModuleRoute><AIStudioDashboard /></ModuleRoute></PrivateRoute>} />
          <Route path="/ai-cofounder" element={<PrivateRoute><ModuleRoute><AICofounderDashboard user={user} /></ModuleRoute></PrivateRoute>} />
          <Route path="/ai-cofounder/outputs" element={<PrivateRoute><ModuleRoute><AICofounderOutputs user={user} /></ModuleRoute></PrivateRoute>} />
          <Route path="/ai-builder/new" element={<Navigate to="/snagbuilder" replace />} />
          <Route path="/create-ai-project" element={<Navigate to="/snagbuilder" replace />} />
          <Route path="/app-builder-existing" element={<PrivateRoute><ModuleRoute><ExistingWebsiteAppBuilder /></ModuleRoute></PrivateRoute>} />
          <Route path="/gpt-pilot" element={<PrivateRoute><ModuleRoute><GPTPilot /></ModuleRoute></PrivateRoute>} />
          <Route path="/ai-engineer" element={<PrivateRoute><ModuleRoute><AIEngineer user={user} /></ModuleRoute></PrivateRoute>} />
          <Route path="/app-builder" element={<PrivateRoute><ModuleRoute><AppBuilder user={user} /></ModuleRoute></PrivateRoute>} />
          <Route path="/snagbuilder" element={<PrivateRoute><ModuleRoute><AliFullStackBuilder user={user} /></ModuleRoute></PrivateRoute>} />
          <Route path="/snag-builder" element={<Navigate to="/snagbuilder" replace />} />
          <Route path="/alifullstack" element={<Navigate to="/snagbuilder" replace />} />
          <Route path="/metagpt" element={<PrivateRoute><ModuleRoute><MetaGPT user={user} /></ModuleRoute></PrivateRoute>} />
          <Route path="/marketing-suite/whatsapp" element={<PrivateRoute><ModuleRoute><WhatsAppMarketingDashboard /></ModuleRoute></PrivateRoute>} />
          <Route path="/support" element={<PrivateRoute><ModuleRoute><SupportTickets /></ModuleRoute></PrivateRoute>} />
          <Route path="/bot-leads" element={<PrivateRoute><ModuleRoute><BotLeadsAndCalls /></ModuleRoute></PrivateRoute>} />
          <Route path="/voice-campaigns" element={<PrivateRoute><ModuleRoute><VoiceCampaigns /></ModuleRoute></PrivateRoute>} />
          <Route path="/snagcart" element={<PrivateRoute><SnagcartPage /></PrivateRoute>} />
          <Route path="/website-configurator" element={<PrivateRoute><OdooConfigurator /></PrivateRoute>} />
          <Route path="/subscription-plans" element={<PrivateRoute><SubscriptionPlans setUser={setUser} /></PrivateRoute>} />
          <Route path="/snagcart/apps" element={<PrivateRoute><SnagcartApps /></PrivateRoute>} />
          <Route path="/platform-guide" element={<PrivateRoute><PlatformGuide /></PrivateRoute>} />
          {/* Website GPT route removed — GPT Pilot now integrated into SiteBrain */}
          <Route path="/w/:slug" element={<WhiteLabelLanding />} />
          <Route path="/settings" element={<PrivateRoute><Settings user={user} setUser={setUser} /></PrivateRoute>} />
          {/* AI Builder route (kept separate, requires manual projectId in URL) */}
          <Route
            path="/ai-builder/:projectId"
            element={
              <PrivateRoute>
                {/* AIBuilderIDE reads projectId from route params via wrapper */}
                <AIBuilderRouteWrapper />
              </PrivateRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </WhitelabelHostGate>
        </SessionCallbackGate>
        </ErrorBoundary>
        <CookieConsentBanner />
        <PlatformHelpWidget />
        <WelcomeTourModal />
        <JessicaChatbot />
        </WhitelabelBrandProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;